import { Checkbox, Grid, IconButton, TextField } from "@material-ui/core";
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import { useGetVehicleTypesQuery } from "features/vehicles/vehicleSlice";
import { orderBy } from "lodash";
import { arrayMove, sortableContainer, sortableElement, sortableHandle } from 'react-sortable-hoc';
import { questionTypes } from "../../formBuilderConsts";
import { FormBuilderStyles } from "../../style";
import { useState, useEffect, useMemo } from "react";
import { debounce } from "lodash";

const QuestionValues = ({
    form,
    question,
    onFieldChange,
    presetValues
}) => {

    const classes = FormBuilderStyles();

    const values = orderBy((presetValues && !question?.values?.length) ? presetValues?.map((v, i) => ({ ...v, value: v.id, order: i })) : [...(Array.isArray(question?.values) ? question?.values : []), ...(presetValues ? [] : [{ value: '', isNew: true, order: question?.values?.length }])] ?? [{}], 'order');

    const getValues = () => {
        return values.filter(v => v.value);
    }

    useEffect(() => {
        // if includePhoto is turned off we also want to set any requirePhoto fields that are true to false
        if (question?.includePhoto !== true) {
            const anyRequirePhoto = values?.some(v => v?.requirePhoto);
            if (anyRequirePhoto) {
                const updated = values?.map((v, index) => ({ ...v, requirePhoto: false, order: index }));
                onFieldChange('values', updated);
            }
        }
    }, [question?.includePhoto]);

    useEffect(() => {
        // if includeNotes is turned off we also want to set any requireNote fields that are true to false
        if (question?.includeNotes !== true) {
            const anyRequireNote = values?.some(v => v?.requireNote);
            if (anyRequireNote) {
                const updated = values?.map((v, index) => ({ ...v, requireNote: false, order: index }));
                onFieldChange('values', updated);
            }
        }
    }, [question?.includeNotes]);

    const onValueChange = (newVal, index, otherProps) => {
        let valuesToSave = getValues();
        const { isFailure, requirePhoto, requireNote, requireActivity, valToMap, desc } = (otherProps ?? {});
        if (index == values?.length - 1) {
            valuesToSave = values;//Saving the new option
            const { isNew, ...others } = valuesToSave[index];
            valuesToSave[index] = others;
        }
        var prevValue = valuesToSave[index];
        valuesToSave[index] = { ...prevValue, value: newVal, isFailure, requirePhoto, requireNote, requireActivity, valToMap, desc };
        onFieldChange('values', valuesToSave?.map((v, index) => ({ ...v, order: index })));
    }

    const onIsFailureChange = (index, option, isFailure) => {
        if (!isFailure) {
            //reset other failure related fields
            onValueChange(option?.value, index, { ...option, isFailure, requireActivity: false });
            return;
        }
        onValueChange(option?.value, index, { ...option, isFailure });
    }

    const onRequirePhotoChange = (index, option, requirePhoto) => {
        onValueChange(option?.value, index, { ...option, requirePhoto });
    }

    const onRequireNoteChange = (index, option, requireNote) => {
        onValueChange(option?.value, index, { ...option, requireNote });
    }

    const onRequireActivityChange = (index, option, requireActivity) => {
        onValueChange(option?.value, index, { ...option, requireActivity: requireActivity });
    }

    const onDescChange = (index, option, desc) => {
        onValueChange(option?.value, index, { ...option, desc });
    }

    const onDeleteValue = (rowIndex) => {
        const valuesToSave = getValues();
        valuesToSave.splice(rowIndex, 1);
        onFieldChange('values', valuesToSave?.map((v, index) => ({ ...v, order: index })));
    }

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const valuesToSave = getValues();
        onFieldChange('values', arrayMove(valuesToSave, oldIndex, newIndex).map((q, index) => ({ ...q, order: index })))
    };

    const mappedToListField = question.questionType === questionTypes.Dropdown &&
        question.mappedToFieldType && question.mappedToField


    return (
        <Grid direction="column" container className={classes.optionsContainer} >
            <div className={classes.optionsContainerTitle}>Options</div>
            <SortableContainer onSortEnd={onSortEnd} useDragHandle>
                {values.map((v, index) => (
                    <SortableItem presetValues={presetValues}
                        index={index} option={v}
                        rowIndex={index}
                        onIsFailureChange={onIsFailureChange}
                        onDescChange={onDescChange}
                        onValueChange={onValueChange}
                        onDeleteValue={onDeleteValue}
                        mappedToListField={mappedToListField}
                        onRequirePhotoChange={onRequirePhotoChange}
                        onRequireNoteChange={onRequireNoteChange}
                        onRequireActivityChange={onRequireActivityChange}
                        question={question}
                    />
                ))}
            </SortableContainer>
        </Grid>
    );
}
export default QuestionValues;


const SortableItem = sortableElement(({ option, rowIndex, onValueChange, onIsFailureChange, onDescChange, onDeleteValue, presetValues, mappedToListField, onRequirePhotoChange, onRequireNoteChange, onRequireActivityChange, question }) => {
    const classes = FormBuilderStyles();
    const DragHandle = sortableHandle(() => <DragIndicatorIcon className={classes.dragIcon} color="secondary" />);

    // Local state for option value
    const [localValue, setLocalValue] = useState(option?.value ?? "");

    useEffect(() => {
        setLocalValue(option?.value ?? "");
    }, [option?.value]);

    const debouncedUpdate = useMemo(() => debounce((value) => {
        onValueChange(value, rowIndex, option);
    }, 300), [onValueChange, rowIndex, option]);

    useEffect(() => {
        return () => debouncedUpdate.cancel();
    }, [debouncedUpdate]);

    return (
        <Grid item alignItems="center" className={classes.multiValueOpt} spacing={1}>
            <Grid container alignItems="center" spacing={1}>
                {option?.isNew ? <div style={{ width: '1.8em' }}></div> : <DragHandle />}
                {!!option?.id &&
                    <Grid item xs={2}><b>{option?.id}</b></Grid>
                }
                {!!option?.id && (<Grid item style={{ maxWidth: 40 }}>Alias:</Grid>)}
                <Grid item xs={4} style={{ maxWidth: 200 }}>
                    <TextField
                        value={localValue}
                        elementType="input"
                        variant='outlined'
                        onChange={e => {
                            setLocalValue(e.target.value);
                            debouncedUpdate(e.target.value);
                        }}
                        onBlur={e => {
                            debouncedUpdate.flush();
                        }}
                        placeholder={option?.isNew ? '+ Type a new option...' : undefined}
                        fullWidth
                    />
                </Grid>
                {!option?.isNew && !presetValues && (
                    <Grid item>
                        <IconButton onClick={() => onDeleteValue(rowIndex)}>
                            <HighlightOffOutlinedIcon style={{ width: '0.7em' }} />
                        </IconButton>
                    </Grid>
                )}
                {!option?.isNew && (
                    <Grid item xs={2}>
                        <label className={classes.isFailure} style={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                                color="primary"
                                checked={option?.isFailure ?? false}
                                onChange={(e) => {
                                    onIsFailureChange(rowIndex, option, e.target.checked);
                                }}
                                style={{ padding: 0, marginRight: 4 }}
                            />
                            Failure
                        </label>
                    </Grid>
                )}

                {option?.isFailure && (
                    <Grid item xs={3}>
                        <TextField
                            value={option?.desc ?? ""}
                            elementType="input"
                            variant='outlined'
                            onChange={e => {
                                onDescChange(rowIndex, option, e.target.value)
                            }}
                            placeholder={'Failure description'}
                        />
                    </Grid>
                )}
                <Grid item xs={2} style={{ marginLeft: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ alignItems: 'center' }}>
                        {(!option?.isNew && question?.includePhoto) && (
                            <label className={classes.isFailure} style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                                <Checkbox
                                    color="primary"
                                    checked={option?.requirePhoto ?? false}
                                    onChange={(e) => {
                                        onRequirePhotoChange(rowIndex, option, e.target.checked);
                                    }}
                                    style={{ padding: 0, marginRight: 4 }}
                                />
                                <span style={{ whiteSpace: 'nowrap' }}>Is Photo Required?</span>
                            </label>
                        )}
                        {(!option?.isNew && question?.includeNotes) && (
                            <label className={classes.isFailure} style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                                <Checkbox
                                    color="primary"
                                    checked={option?.requireNote ?? false}
                                    onChange={(e) => {
                                        onRequireNoteChange(rowIndex, option, e.target.checked);
                                    }}
                                    style={{ padding: 0, marginRight: 4 }}
                                />
                                <span style={{ whiteSpace: 'nowrap' }}>Is Note Required?</span>
                            </label>
                        )}
                        {(!option?.isNew && option?.isFailure) && (
                            <label className={classes.isFailure} style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                                <Checkbox
                                    color="primary"
                                    checked={option?.requireActivity ?? false}
                                    onChange={(e) => {
                                        onRequireActivityChange(rowIndex, option, e.target.checked);
                                    }}
                                    style={{ padding: 0, marginRight: 4 }}
                                />
                                <span style={{ whiteSpace: 'nowrap' }}>Is Activity Required?</span>
                            </label>
                        )}
                    </div>
                </Grid>
                {/* <Grid item>
                {!option?.isNew && mappedToListField &&
                    //TODO: Make generic
                    <ListFieldMapping
                        value={option?.valToMap}
                        onChangeVal={val => onValueChange(option?.value ?? "", rowIndex, +val)}
                        values={vehicleTypes}
                    />}
            </Grid> */}
            </Grid>
        </Grid>
    )
});

const SortableContainer = sortableContainer(({ children }) => {
    return <Grid container direction="column" >
        {children}
    </Grid>;
});